export type PriceBookCalcFlow =
  | 'STANDARD'
  | 'MOUNT_COLOR_RANGES'
  | 'BRANCHED_CORNICE'
  | 'BRANCHED_CORNICE_DIRECT'
  | 'ROW_ONLY'
  | 'TEXTILE';

export type PriceBookMechanismMode =
  | 'INHERIT'
  | 'NONE'
  | 'FIELDS_ONLY'
  | 'MOTORS_ONLY';

export type PriceBookMechanismKind = 'NONE' | 'MOTORS' | 'FIELDS';

export interface PriceBookMechanismTypeLike {
  id: string;
  scoped?: boolean;
}

export interface PriceBookMechanismFieldLike {
  corniceDefaultEnabled?: boolean;
  corniceBindings?: ReadonlyArray<MechanismCorniceBinding>;
}

export interface MechanismCorniceBinding {
  sectionCode: string;
  corniceKey: string;
  enabled: boolean;
}

export interface PriceBookMechanismTypeWithFieldsLike {
  kind: PriceBookMechanismKind;
  fields?: ReadonlyArray<PriceBookMechanismFieldLike> | null;
}

export interface MountColorRangesSectionLike {
  code?: string | null;
  calcOptionRole?: string | null;
  calcFlow?: string | null;
  baseCorniceName?: string | null;
}

/** A named fixed cornice in a branched flow: price options select its mechanism.
 * Empty name preserves the existing branch-as-cornice contract. No name/code guessing. */
export function fixedCorniceNameOf(
  section: MountColorRangesSectionLike | null | undefined,
  options?: ReadonlyArray<{ key?: string; label: string; hidden?: boolean; pricedMechanismTypeId?: string }>,
  selectedKey?: string | null,
): string | null {
  const name = isBranchedCorniceFlow(section) ? section?.baseCorniceName?.trim() || null : null;
  const selected = selectedKey ? options?.find(option => option.key === selectedKey) : undefined;
  const visible = selected ? [selected] : options?.filter(option => !option.hidden) ?? [];
  // After tariff consolidation the visible price option is the cornice itself.
  // The old section name must not turn it into a second, fictitious mechanism.
  // Hidden historical tariffs and explicit priced mechanisms keep their meaning.
  if (name && visible.length > 0 && visible.every(option =>
    !option.pricedMechanismTypeId && option.label.trim() === name)) return null;
  return name;
}

export interface MechanismCorniceOptionLike {
  bindingKey?: string;
  corniceId?: string;
  corniceName?: string;
  label: string;
  groupLabel?: string | null;
  colorLabel?: string | null;
  corniceColors?: ReadonlyArray<string> | null;
  /** Самостоятельная конечная ветка товара: механизма у неё быть не может. */
  withoutCornice?: boolean | null;
}

export interface MechanismBranchOptionLike {
  label?: string | null;
  groupLabel?: string | null;
  mechanismMode?: PriceBookMechanismMode | string | null;
  mechanismTypeIds?: ReadonlyArray<string> | null;
  withoutCornice?: boolean | null;
}

export interface MechanismBranchRowLike extends PriceBookMechanismRowLike {
  calcHidden?: boolean | null;
}

/**
 * Единое распознавание самостоятельной конечной ветки «Без карниза».
 *
 * Новые прайс-книги передают технический флаг. Для старых версий offer и
 * исторических связок сохраняем совместимость по пользовательскому названию:
 * такая ветка завершает конфигурацию и никогда не открывает механизм.
 */
export function isWithoutCorniceOption(
  option: Pick<MechanismBranchOptionLike, 'withoutCornice' | 'label' | 'groupLabel'> | null | undefined,
): boolean {
  return !!option && (
    option.withoutCornice === true
    || /без\s+карниз/iu.test(option.label ?? '')
    || /без\s+карниз/iu.test(option.groupLabel ?? '')
  );
}

/**
 * Стабильный ключ строки из «Связки → Типы и поля механизма».
 * Название, цвет и подпись диапазона — только представление. Источник связи —
 * bindingKey оффера (logicalId первой колонки опции) либо id отдельного карниза.
 */
export function mechanismCorniceKeyOf(
  option: MechanismCorniceOptionLike | null | undefined,
): string | null {
  if (!option) return null;
  if (typeof option.bindingKey === 'string' && option.bindingKey.trim()) return option.bindingKey;
  if ('corniceId' in option && typeof option.corniceId === 'string') return `cornice:${option.corniceId}`;
  return null;
}

/**
 * Универсальная трёхуровневая цепочка:
 * вид монтажа → цвет карниза → размерная ступень/цена.
 *
 * Проверка идёт по явному полю calcFlow, а не по названию нового вида.
 * Фолбэк по коду PLISSE оставлен только для старых offer/тестовых снимков,
 * созданных до появления calcFlow.
 */
export function isMountColorRangesFlow(
  section: MountColorRangesSectionLike | null | undefined,
): boolean {
  if (section?.calcOptionRole !== 'MOUNT_BASE') return false;
  return section.calcFlow === 'MOUNT_COLOR_RANGES'
    || (section.calcFlow == null && section.code === 'PLISSE');
}

/**
 * Ветвящийся карниз: у каждой верхнеуровневой опции свой следующий шаг.
 * Например, одна ветка открывает механизмы, вторая — цвет и бинарную планку,
 * третья заканчивает расчёт без дополнительных полей.
 */
export function isBranchedCorniceFlow(
  section: MountColorRangesSectionLike | null | undefined,
): boolean {
  return section?.calcOptionRole === 'MOUNT_BASE'
    && (
      section.calcFlow === 'BRANCHED_CORNICE'
      || section.calcFlow === 'BRANCHED_CORNICE_DIRECT'
    );
}

/**
 * Ветвящийся карниз самостоятельного изделия без пользовательского выбора
 * строки прайса. Единственная техническая строка остаётся источником цены,
 * но после выбора вида замерщик сразу переходит к веткам карниза.
 */
export function isDirectBranchedCorniceFlow(
  section: MountColorRangesSectionLike | null | undefined,
): boolean {
  return section?.calcOptionRole === 'MOUNT_BASE'
    && section.calcFlow === 'BRANCHED_CORNICE_DIRECT';
}

/**
 * Самостоятельный товар, где пользователь выбирает только строку прайса.
 * Единственная расчётная колонка назначается автоматически и не выдаётся за
 * карниз или другой дополнительный параметр.
 */
export function isRowOnlyFlow(
  section: MountColorRangesSectionLike | null | undefined,
): boolean {
  return section?.calcFlow === 'ROW_ONLY';
}

/** Оба data-driven flow хранят цвета непосредственно на расчётной опции. */
export function usesOptionCorniceColors(
  section: MountColorRangesSectionLike | null | undefined,
): boolean {
  return isMountColorRangesFlow(section) || isBranchedCorniceFlow(section);
}

/**
 * Ограничение механизма задаётся семантикой ветки, а не её названием.
 * Это позволяет, например, оставить у алюминиевого карниза только
 * механические поля, даже если глобально доступны и электронные моторы.
 */
export function mechanismModeAllowsKind(
  mode: PriceBookMechanismMode | string | null | undefined,
  kind: PriceBookMechanismKind,
): boolean {
  if (mode === 'NONE') return kind === 'NONE';
  if (mode === 'FIELDS_ONLY') return kind === 'FIELDS';
  if (mode === 'MOTORS_ONLY') return kind === 'MOTORS';
  return true;
}

/**
 * Конкретные типы механизма можно привязать к одной ветке карниза.
 *
 * Пустой список сохраняет прежнее поведение: доступны все обычные глобальные
 * типы, а scoped-типы (созданные для специальных конструкторов) не протекают
 * в остальные виды жалюзи. Непустой список является точным allow-list.
 */
export function mechanismTypeAllowedForBranch(
  type: PriceBookMechanismTypeLike,
  mechanismTypeIds: ReadonlyArray<string> | null | undefined,
): boolean {
  const ids = (mechanismTypeIds ?? []).filter(Boolean);
  return ids.length > 0 ? ids.includes(type.id) : !type.scoped;
}

/**
 * Единый контракт матрицы связок и калькулятора: конечная ветка
 * «Без карниза» никогда не предлагает типы/поля/моторы. Для остальных веток
 * учитываются режим механизма и точные allow-list строки/ветки.
 */
export function mechanismOptionLinkedToType(
  option: MechanismBranchOptionLike,
  type: PriceBookMechanismTypeLike & { kind: PriceBookMechanismKind },
  rows: ReadonlyArray<MechanismBranchRowLike> = [],
): boolean {
  if (isWithoutCorniceOption(option)) return false;
  if (!mechanismModeAllowsKind(option.mechanismMode, type.kind)) return false;
  const visibleRows = rows.filter((row) => !row.calcHidden);
  if (rows.length > 0 && visibleRows.length === 0) return false;
  if (!visibleRows.length) {
    return mechanismTypeAllowedForBranch(type, option.mechanismTypeIds);
  }
  return visibleRows.some((row) => mechanismTypeAllowedForBranch(
    type,
    mechanismTypeIdsForPriceRow(rows, row.id, option.mechanismTypeIds),
  ));
}

/**
 * Поле механического типа доступно для выбранного карниза, если оно не
 * ограничено связками либо текущий стабильный ключ карниза есть в allow-list.
 *
 * Точная связь «код раздела + стабильный ключ карниза» — единственный источник.
 * Для новой ветки без записи действует явный corniceDefaultEnabled.
 */
export function mechanismFieldVisibleForCornice(
  field: PriceBookMechanismFieldLike,
  corniceKey: string | null | undefined,
  sectionCode?: string | null,
): boolean {
  const binding = field.corniceBindings?.find((candidate) =>
    candidate.sectionCode === sectionCode && candidate.corniceKey === corniceKey);
  if (binding) return binding.enabled;
  return field.corniceDefaultEnabled ?? true;
}

/** Изменяет только одну пару, не материализуя/переписывая старые глобальные связи. */
export function setMechanismFieldCorniceBinding<T extends PriceBookMechanismFieldLike>(
  field: T,
  sectionCode: string,
  corniceKey: string,
  enabled: boolean,
): T & { corniceBindings: MechanismCorniceBinding[] } {
  return {
    ...field,
    corniceBindings: [
      ...(field.corniceBindings ?? []).filter((binding) =>
        binding.sectionCode !== sectionCode || binding.corniceKey !== corniceKey),
      { sectionCode, corniceKey, enabled },
    ],
  };
}

/**
 * Механический FIELDS-тип не показывается целиком, когда администратор снял
 * все его галочки у выбранного карниза. Для NONE и MOTORS действуют собственные
 * правила доступности, поэтому они здесь не ограничиваются.
 */
export function mechanismTypeAvailableForCornice(
  type: PriceBookMechanismTypeWithFieldsLike,
  corniceKey: string | null | undefined,
  sectionCode?: string | null,
): boolean {
  return type.kind !== 'FIELDS'
    || (type.fields ?? []).some((field) =>
      mechanismFieldVisibleForCornice(field, corniceKey, sectionCode));
}

export interface PriceBookMechanismRowLike {
  id: string;
  parentRowId?: string | null;
  mechanismTypeIds?: ReadonlyArray<string> | null;
}

/**
 * Разрешённые типы для выбранной позиции:
 * позиция → её родительская лента → ветка карниза.
 * Так один вид/цвет может иметь собственный механизм, не меняя соседние товары.
 */
export function mechanismTypeIdsForPriceRow(
  rows: ReadonlyArray<PriceBookMechanismRowLike>,
  rowId: string | null | undefined,
  branchTypeIds: ReadonlyArray<string> | null | undefined,
): string[] {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const selected = rowId ? byId.get(rowId) : undefined;
  const own = selected?.mechanismTypeIds?.filter(Boolean) ?? [];
  if (own.length) return [...own];
  const parent = selected?.parentRowId ? byId.get(selected.parentRowId) : undefined;
  const inherited = parent?.mechanismTypeIds?.filter(Boolean) ?? [];
  if (inherited.length) return [...inherited];
  return [...(branchTypeIds ?? []).filter(Boolean)];
}
